// ============================================================================
// OrgTreeNode — rendu recursif d'un noeud de l'arbre (E1c, F-238 / NAV v2)
// ============================================================================

import type { OrgChildGroup, OrgNode } from '../../lib/org/hierarchy';
import { OrgNodeCard } from './OrgNodeCard';

export function OrgTreeNode({
  node,
  depth,
  collapsed,
  highlightedEmail,
  focusedEmail,
  onToggle,
  onDrillIn,
}: {
  node: OrgNode;
  depth: number;
  collapsed: ReadonlySet<string>;
  highlightedEmail?: string | null;
  focusedEmail?: string | null;
  onToggle: (email: string) => void;
  onDrillIn: (email: string) => void;
}) {
  const isCollapsed = collapsed.has(node.email);

  return (
    <li>
      <OrgNodeCard
        node={node}
        depth={depth}
        isCollapsed={isCollapsed}
        isHighlighted={highlightedEmail === node.email}
        isFocused={focusedEmail === node.email}
        onToggle={() => onToggle(node.email)}
        onDrillIn={() => onDrillIn(node.email)}
      />
      {node.children.length > 0 && !isCollapsed ? (
        <ul className="transition-opacity duration-200 ease-out">
          {node.children.map((group) =>
            group.team_label === null ? (
              group.children.map((child) => (
                <OrgTreeNode
                  key={child.email}
                  node={child}
                  depth={depth + 1}
                  collapsed={collapsed}
                  highlightedEmail={highlightedEmail}
                  focusedEmail={focusedEmail}
                  onToggle={onToggle}
                  onDrillIn={onDrillIn}
                />
              ))
            ) : (
              <TeamGroup
                key={group.team_id ?? group.team_label}
                group={group}
                depth={depth}
                collapsed={collapsed}
                highlightedEmail={highlightedEmail}
                focusedEmail={focusedEmail}
                onToggle={onToggle}
                onDrillIn={onDrillIn}
              />
            ),
          )}
        </ul>
      ) : null}
    </li>
  );
}

/** Boite d'etiquette d'equipe + sous-rangee (enfants rattaches a une AUTRE equipe). */
function TeamGroup({
  group,
  depth,
  collapsed,
  highlightedEmail,
  focusedEmail,
  onToggle,
  onDrillIn,
}: {
  group: OrgChildGroup;
  depth: number;
  collapsed: ReadonlySet<string>;
  highlightedEmail?: string | null;
  focusedEmail?: string | null;
  onToggle: (email: string) => void;
  onDrillIn: (email: string) => void;
}) {
  return (
    <li>
      <span className="whitespace-nowrap rounded-md border border-brand-200 bg-brand-50 px-2.5 py-1 text-chip font-medium text-brand-700 dark:border-brand-900 dark:bg-brand-950/40 dark:text-brand-300">
        {group.team_label}
      </span>
      <ul>
        {group.children.map((child) => (
          <OrgTreeNode
            key={child.email}
            node={child}
            depth={depth + 1}
            collapsed={collapsed}
            highlightedEmail={highlightedEmail}
            focusedEmail={focusedEmail}
            onToggle={onToggle}
            onDrillIn={onDrillIn}
          />
        ))}
      </ul>
    </li>
  );
}
